import {
  addMonths,
  addYears,
  dayOfMonth,
  daysBetween,
  financialYearEnd,
  financialYearOf,
  type IsoDate,
  type IsoMonth,
  monthsBetween,
} from './calendar';
import { lateFee, type TurnoverBand } from './late-fees';
import type { Paise } from './money';

/**
 * Which GST returns fall due when, and where each stands.
 *
 * Monthly filers: GSTR-1 on the 11th (Notification 83/2020-CT) and GSTR-3B on the 20th (rule 61) of the
 * next month. Quarterly (QRMP) filers: GSTR-1 on the 13th after the quarter and GSTR-3B on the 22nd or
 * 24th, by state (rule 61(1)(ii)). Composition: CMP-08 on the 18th after the quarter (rule 62) and
 * GSTR-4 on 30 June after the financial year (30 April up to 2023-24; Notification 12/2024-CT). The
 * government extends dates now and then; those come in as dated overrides, so they change without a
 * release. A return can't be filed more than three years after its due date (sections 37(5) and
 * 39(11) of the CGST Act, in force from 1 October 2023): it's time-barred.
 */

export const RETURN_FORMS = ['gstr1', 'gstr3b', 'cmp08', 'gstr4'] as const;
export type ReturnForm = (typeof RETURN_FORMS)[number];

/** How a registration files: monthly, quarterly under QRMP, or as a composition taxpayer. */
export const FILER_KINDS = ['monthly', 'quarterly', 'composition'] as const;
export type FilerKind = (typeof FILER_KINDS)[number];

export type PeriodKind = 'month' | 'quarter' | 'year';

/** GSTN's names for the forms (the `rtntype` in its return tracking). */
export const GSTN_RETURN_TYPES: Record<ReturnForm, string> = {
  gstr1: 'GSTR1',
  gstr3b: 'GSTR3B',
  cmp08: 'CMP08',
  gstr4: 'GSTR4',
};

export function formFromGstn(rtntype: string): ReturnForm | null {
  const key = rtntype.replace(/[\s-]/g, '').toUpperCase();
  const found = (Object.entries(GSTN_RETURN_TYPES) as [ReturnForm, string][]).find(([, name]) => name === key);
  return found ? found[0] : null;
}

/** How forms are written for people: "GSTR-1", "GSTR-3B", "CMP-08", "GSTR-4". */
export const FORM_LABELS: Record<ReturnForm, string> = {
  gstr1: 'GSTR-1',
  gstr3b: 'GSTR-3B',
  cmp08: 'CMP-08',
  gstr4: 'GSTR-4',
};

/**
 * States and union territories whose quarterly (QRMP) GSTR-3B is due on the 22nd; the rest file by the
 * 24th. Proviso to rule 61(5) of the CGST Rules. State codes as in the GSTIN; 25 (Daman and Diu) was
 * merged into 26 in 2020 and stays listed for old registrations.
 */
export const QRMP_22ND_STATES: ReadonlySet<string> = new Set([
  '22', // Chhattisgarh
  '23', // Madhya Pradesh
  '24', // Gujarat
  '25', // Daman and Diu
  '26', // Dadra and Nagar Haveli and Daman and Diu
  '27', // Maharashtra
  '29', // Karnataka
  '30', // Goa
  '31', // Lakshadweep
  '32', // Kerala
  '33', // Tamil Nadu
  '34', // Puducherry
  '35', // Andaman and Nicobar Islands
  '36', // Telangana
  '37', // Andhra Pradesh
]);

export const QRMP_24TH_STATES: ReadonlySet<string> = new Set([
  '01', // Jammu and Kashmir
  '02', // Himachal Pradesh
  '03', // Punjab
  '04', // Chandigarh
  '05', // Uttarakhand
  '06', // Haryana
  '07', // Delhi
  '08', // Rajasthan
  '09', // Uttar Pradesh
  '10', // Bihar
  '11', // Sikkim
  '12', // Arunachal Pradesh
  '13', // Nagaland
  '14', // Manipur
  '15', // Mizoram
  '16', // Tripura
  '17', // Meghalaya
  '18', // Assam
  '19', // West Bengal
  '20', // Jharkhand
  '21', // Odisha
  '38', // Ladakh
]);

/** The 22nd or the 24th. Registrations in neither list (97 Other Territory, 99 Centre) get the earlier date. */
export function qrmpGstr3bDay(stateCode: string): 22 | 24 {
  return QRMP_24TH_STATES.has(stateCode) ? 24 : 22;
}

const QUARTER_END_MONTHS = new Set([3, 6, 9, 12]);

export function isQuarterEnd(month: IsoMonth): boolean {
  return QUARTER_END_MONTHS.has(Number(month.slice(5, 7)));
}

/** The quarter-end month (June, September, December or March) of the quarter a month is in. */
export function quarterEndOf(month: IsoMonth): IsoMonth {
  const m = Number(month.slice(5, 7));
  return addMonths(month, (3 - (m % 3)) % 3);
}

/** A return that falls due: which form, for which period (its last month), and when. */
export interface ReturnDue {
  form: ReturnForm;
  /** The period's last month: the month itself, a quarter's last month, or March for a year. */
  period: IsoMonth;
  periodKind: PeriodKind;
  dueOn: IsoDate;
  /** The date the rules give, when an override moved it. */
  normalDueOn: IsoDate;
  /** The override that set `dueOn`, if one did. */
  override: { id: string; reason: string | null; reference: string | null } | null;
}

/**
 * A government extension (or any change) to one return's due date. Overrides are never edited: a new
 * one replaces an old one, and the newest that matches wins; a withdrawn one no longer counts.
 */
export interface DueDateOverride {
  id: string;
  form: ReturnForm;
  period: IsoMonth;
  /** Only for this kind of filer (null: everyone who files this form). */
  filer: FilerKind | null;
  /** Only for these states (null: all). */
  stateCodes: readonly string[] | null;
  dueOn: IsoDate;
  reason: string | null;
  /** e.g. "Notification 01/2025-Central Tax". */
  reference: string | null;
  /** ISO time it was added: the newest matching one wins. */
  createdAt: string;
}

export interface Filer {
  kind: FilerKind;
  stateCode: string;
}

/** The due date the rules give, before any override. */
export function normalDueDate(form: ReturnForm, period: IsoMonth, filer: Filer): IsoDate {
  const next = addMonths(period, 1);
  switch (form) {
    case 'gstr1':
      return dayOfMonth(next, filer.kind === 'quarterly' ? 13 : 11);
    case 'gstr3b':
      return dayOfMonth(next, filer.kind === 'quarterly' ? qrmpGstr3bDay(filer.stateCode) : 20);
    case 'cmp08':
      return dayOfMonth(next, 18);
    case 'gstr4':
      // 30 June for 2024-25 on; 30 April before.
      return Number(period.slice(0, 4)) >= 2025 ? dayOfMonth(addMonths(period, 3), 30) : dayOfMonth(next, 30);
  }
}

/** The override that applies to a return, if any: the newest one that matches it. */
export function overrideFor(
  form: ReturnForm,
  period: IsoMonth,
  filer: Filer,
  overrides: readonly DueDateOverride[],
): DueDateOverride | null {
  let best: DueDateOverride | null = null;
  for (const o of overrides) {
    if (o.form !== form || o.period !== period) continue;
    if (o.filer && o.filer !== filer.kind) continue;
    if (o.stateCodes && !o.stateCodes.includes(filer.stateCode)) continue;
    if (!best || o.createdAt > best.createdAt) best = o;
  }
  return best;
}

export function dueDate(
  form: ReturnForm,
  period: IsoMonth,
  filer: Filer,
  overrides: readonly DueDateOverride[] = [],
): ReturnDue {
  const normalDueOn = normalDueDate(form, period, filer);
  const override = overrideFor(form, period, filer, overrides);
  return {
    form,
    period,
    periodKind: form === 'gstr4' ? 'year' : filer.kind === 'monthly' ? 'month' : 'quarter',
    dueOn: override?.dueOn ?? normalDueOn,
    normalDueOn,
    override: override ? { id: override.id, reason: override.reason, reference: override.reference } : null,
  };
}

/** The forms a filer files, in the order they're filed. */
export function formsFor(kind: FilerKind): ReturnForm[] {
  return kind === 'composition' ? ['cmp08', 'gstr4'] : ['gstr1', 'gstr3b'];
}

/**
 * Every return a filer has due for the months `from` to `to` (inclusive, YYYY-MM), in order of period
 * then form. A period is included when its last month is in range: a quarter by its last month, a
 * GSTR-4 by its March.
 */
export function returnSchedule(
  filer: Filer,
  from: IsoMonth,
  to: IsoMonth,
  overrides: readonly DueDateOverride[] = [],
): ReturnDue[] {
  const due: ReturnDue[] = [];
  const months = monthsBetween(from, to);
  for (let i = 0; i <= months; i++) {
    const month = addMonths(from, i);
    const quarterEnd = isQuarterEnd(month);
    if (filer.kind === 'monthly') {
      due.push(dueDate('gstr1', month, filer, overrides), dueDate('gstr3b', month, filer, overrides));
    } else if (filer.kind === 'quarterly') {
      if (quarterEnd) due.push(dueDate('gstr1', month, filer, overrides), dueDate('gstr3b', month, filer, overrides));
    } else {
      if (quarterEnd) due.push(dueDate('cmp08', month, filer, overrides));
      if (month === financialYearEnd(financialYearOf(month))) due.push(dueDate('gstr4', month, filer, overrides));
    }
  }
  return due;
}

/** The last day a return can be filed: three years from its due date. */
export function lastFilingDate(dueOn: IsoDate): IsoDate {
  return addYears(dueOn, 3);
}

export function isTimeBarred(dueOn: IsoDate, today: IsoDate): boolean {
  return today > lastFilingDate(dueOn);
}

/** A return filed, as the GST portal (or EkApp) reports it. */
export interface FiledReturn {
  form: ReturnForm;
  period: IsoMonth;
  filedOn: IsoDate;
}

/**
 * filed: done. late: past its due date and not filed (late fees add up daily). upcoming: not due yet
 * (or due today). time_barred: not filed, and it's more than three years past its due date, so it can't
 * be filed any more.
 */
export type ReturnState = 'filed' | 'late' | 'upcoming' | 'time_barred';

export interface ReturnStatus extends ReturnDue {
  state: ReturnState;
  filedOn: IsoDate | null;
  /** Days it was filed after its due date (0 when on time), for filed returns. */
  filedDaysLate: number | null;
  /** Days past the due date so far, for late ones. */
  daysLate: number | null;
  /** Days until the due date (0: due today), for upcoming ones. */
  daysLeft: number | null;
  /** Late fee so far, in paise, for late ones: an estimate until it's filed. */
  lateFee: Paise;
}

export interface StatusOptions {
  today: IsoDate;
  /** Treat this return as a NIL return (a lower late fee), e.g. when the person said there was nothing to report. */
  isNil?: (due: ReturnDue) => boolean;
  /** The aggregate annual turnover band, which caps late fees (unknown: up to ₹1.5 crore). */
  turnoverBand?: TurnoverBand;
}

/** Where each return stands on a day, from its due date and whether (and when) it was filed. */
export function returnStatuses(
  schedule: readonly ReturnDue[],
  filed: readonly FiledReturn[],
  options: StatusOptions,
): ReturnStatus[] {
  const filedByKey = new Map(filed.map((f) => [`${f.form}:${f.period}`, f]));
  return schedule.map((due) => {
    const done = filedByKey.get(`${due.form}:${due.period}`);
    if (done) {
      return {
        ...due,
        state: 'filed',
        filedOn: done.filedOn,
        filedDaysLate: Math.max(0, daysBetween(due.dueOn, done.filedOn)),
        daysLate: null,
        daysLeft: null,
        lateFee: 0,
      };
    }
    const past = daysBetween(due.dueOn, options.today);
    if (past <= 0) {
      const daysLeft = daysBetween(options.today, due.dueOn);
      return { ...due, state: 'upcoming', filedOn: null, filedDaysLate: null, daysLate: null, daysLeft, lateFee: 0 };
    }
    if (isTimeBarred(due.dueOn, options.today)) {
      return {
        ...due,
        state: 'time_barred',
        filedOn: null,
        filedDaysLate: null,
        daysLate: past,
        daysLeft: null,
        lateFee: 0,
      };
    }
    return {
      ...due,
      state: 'late',
      filedOn: null,
      filedDaysLate: null,
      daysLate: past,
      daysLeft: null,
      lateFee: lateFee({
        form: due.form,
        daysLate: past,
        nil: options.isNil?.(due) ?? false,
        ...(options.turnoverBand ? { turnoverBand: options.turnoverBand } : {}),
      }),
    };
  });
}
